import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Add, Delete } from '@mui/icons-material';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { FC } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import {
  blankPatternForm, PALETTE_COLORS, PatternFormValues, schema, slotSpanSeconds, toFormValues,
} from 'src/pages/TruckShifts/patternForm';
import { TimeSelectField } from 'src/pages/TruckShifts/presenters/TimeSelectField.presenter';

type Props = {
  open: boolean;
  initialPattern?: ShiftPatternEntity;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: PatternFormValues) => void | Promise<void>;
  onDelete?: () => void;
};

export const PatternEditorDialog: FC<Props> = ({ open, initialPattern, submitting, onClose, onSubmit, onDelete }) => {
  const { control, handleSubmit, watch, setValue, formState: { errors, isDirty } } = useForm<PatternFormValues>({
    resolver: yupResolver(schema),
    defaultValues: initialPattern ? toFormValues(initialPattern) : blankPatternForm(),
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'slots' });
  const multiDay = watch('multiDay');
  const selectedColor = watch('color');
  const watchedName = watch('name');
  const watchedSlots = watch('slots');
  const slotsError = typeof errors.slots?.message === 'string' ? errors.slots.message : undefined;
  const firstSlot = (watchedSlots ?? [])[0];

  const handleClose = () => {
    if (isDirty && !window.confirm('変更を破棄してもよろしいですか？')) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialPattern ? '便を編集' : '便を追加'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="便名"
                size="small"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              稼働時間（複数稼働がある場合は「時間を追加」／労働時間は枠ごとに指定）
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              ※ 終了が開始以前の場合は自動で翌日扱いになります
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {fields.map((f, index) => {
                const slot = (watchedSlots ?? [])[index];
                const span = slot ? slotSpanSeconds(slot.startTime, slot.endTime) : null;
                const maxHours = span === null ? undefined : span / 3600;
                return (
                  <Stack key={f.id} direction="row" spacing={1} alignItems="center">
                    <Controller
                      name={`slots.${index}.startTime`}
                      control={control}
                      render={({ field }) => (
                        <TimeSelectField
                          label="開始"
                          value={field.value}
                          onChange={field.onChange}
                          sx={{ flex: 1 }}
                          error={!!errors.slots?.[index]?.startTime}
                        />
                      )}
                    />
                    <Typography>〜</Typography>
                    <Controller
                      name={`slots.${index}.endTime`}
                      control={control}
                      render={({ field }) => (
                        <TimeSelectField
                          label="終了"
                          value={field.value}
                          onChange={field.onChange}
                          sx={{ flex: 1 }}
                          error={!!errors.slots?.[index]?.endTime}
                        />
                      )}
                    />
                    <Controller
                      name={`slots.${index}.workingHours`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { field.onChange(''); return; }
                            const parsed = Number(raw);
                            if (Number.isNaN(parsed)) return;
                            const clamped = maxHours === undefined ? Math.max(0, parsed) : Math.min(Math.max(0, parsed), maxHours);
                            field.onChange(clamped);
                          }}
                          type="number"
                          size="small"
                          label="労働時間"
                          sx={{ width: 140, flexShrink: 0 }}
                          error={!!errors.slots?.[index]?.workingHours}
                          helperText={errors.slots?.[index]?.workingHours?.message}
                          InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
                          inputProps={{ min: 0, max: maxHours, step: 0.5 }}
                        />
                      )}
                    />
                    {index > 0 ? (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => remove(index)}
                        aria-label="スロット削除"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 34 }} />
                    )}
                  </Stack>
                );
              })}
              <Button
                size="small"
                startIcon={<Add />}
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => append({ startTime: '13:00', endTime: '17:00', workingHours: '' })}
              >
                時間を追加
              </Button>
              {slotsError && <Typography variant="caption" color="error">{slotsError}</Typography>}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Controller
              name="multiDay"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="出っぱなし（複数日）"
                />
              )}
            />
            {multiDay && (
              <Controller
                name="multiDayLength"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                    type="number"
                    size="small"
                    label="日数"
                    sx={{ width: 90 }}
                    error={!!errors.multiDayLength}
                    helperText={errors.multiDayLength?.message}
                    inputProps={{ min: 2 }}
                  />
                )}
              />
            )}
          </Stack>

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary">表示色</Typography>
            <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
              {PALETTE_COLORS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setValue('color', c, { shouldDirty: true })}
                  aria-label={`色 ${c}`}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: c,
                    cursor: 'pointer',
                    border: selectedColor === c ? '3px solid #1e293b' : '2px solid #fff',
                    boxShadow: '0 0 0 1px #e2e8f0',
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: '2px solid',
              borderColor: selectedColor,
              backgroundColor: `color-mix(in srgb, ${selectedColor} 14%, #fff)`,
            }}
          >
            <Typography variant="caption" color="text.secondary" component="div">プレビュー</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: selectedColor }} />
              <Typography variant="body2" fontWeight={700}>{watchedName || '(名前未入力)'}</Typography>
              {firstSlot && (
                <Typography variant="caption" color="text.secondary">{firstSlot.startTime}-{firstSlot.endTime}</Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {initialPattern && onDelete && (
            <Button color="error" startIcon={<Delete />} onClick={onDelete}>削除</Button>
          )}
        </Box>
        <Box>
          <Button onClick={handleClose}>キャンセル</Button>
          <Button variant="contained" disabled={submitting} onClick={() => { void handleSubmit(onSubmit)(); }}>保存</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
